import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Radio, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import {
  BidClassifications,
  RuleDefinitionData,
  RuleTemplate,
  createCampaignRules,
  listRuleTemplates,
  updatePrimaryMetrics,
  updateRuleClassifications,
  updateRuleDefinitions,
} from '~/services/PublicOfferFeedService';
import { formatDateWithClock } from '~/services/TimeService';

const PAGE_SIZE = 10;

interface LoadTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  adGroupId?: string;
  hasRules: boolean;
  onSuccess: () => void;
}

export default function LoadTemplateDialog({
  open,
  onClose,
  campaignId,
  adGroupId,
  hasRules,
  onSuccess,
}: LoadTemplateDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [templates, setTemplates] = useState<Array<RuleTemplate>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTemplates = useCallback(
    async (currentPage: number) => {
      setIsLoading(true);
      try {
        const response = await listRuleTemplates({ page: currentPage, limit: PAGE_SIZE });
        setTemplates(response.result?.templates || []);
        setTotalCount(response.result?.count || 0);
      } catch (error) {
        enqueueSnackbar('Failed to load templates', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    if (open) {
      setPage(0);
      setSelectedTemplateId(null);
      fetchTemplates(0);
    }
  }, [open, fetchTemplates]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchTemplates(newPage);
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setSelectedTemplateId(null);
      onClose();
    }
  }, [onClose, isSubmitting]);

  const handleLoad = useCallback(async () => {
    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
    if (!selectedTemplate) {
      return;
    }

    setIsSubmitting(true);
    try {
      const { primary_metrics, rule_definitions, rule_classifications } = selectedTemplate;

      if (hasRules) {
        await updatePrimaryMetrics(campaignId, primary_metrics, adGroupId);
      } else {
        await createCampaignRules(campaignId, primary_metrics, adGroupId);
      }

      await updateRuleDefinitions({
        campaignId,
        adGroupId,
        ruleDefinitions: rule_definitions as RuleDefinitionData,
      });

      await updateRuleClassifications({
        campaignId,
        adGroupId,
        ruleClassifications: rule_classifications as BidClassifications,
      });

      enqueueSnackbar('Template loaded successfully!', { variant: 'success' });
      onSuccess();
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      enqueueSnackbar(`Failed to load template: ${errorMessage}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [templates, selectedTemplateId, hasRules, campaignId, adGroupId, enqueueSnackbar, onSuccess, handleClose]);

  const handleRowClick = useCallback((params: { row: RuleTemplate }) => {
    setSelectedTemplateId(params.row.id);
  }, []);

  const columns: Array<GridColDef<RuleTemplate>> = useMemo(
    () => [
      {
        field: 'select',
        headerName: '',
        width: 50,
        sortable: false,
        renderCell: ({ row }) => (
          <Radio
            size="small"
            checked={selectedTemplateId === row.id}
            onChange={() => setSelectedTemplateId(row.id)}
            sx={{ p: 0 }}
          />
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'primary_metrics',
        headerName: 'Primary Metrics',
        flex: 1.5,
        minWidth: 200,
        valueGetter: (value: Array<string>) => value?.join(', ') || '',
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 200,
        valueGetter: (value: string) => (value ? formatDateWithClock(value) : ''),
      },
    ],
    [selectedTemplateId],
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Load Template</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        {!isLoading && totalCount === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No templates available. Create a template first by saving your current rule configuration.
          </Alert>
        )}

        {(isLoading || totalCount > 0) && (
          <DataGrid
            rows={templates}
            columns={columns}
            loading={isLoading || isSubmitting}
            density="compact"
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={{ page, pageSize: PAGE_SIZE }}
            onPaginationModelChange={({ page: newPage }) => handlePageChange(newPage)}
            pageSizeOptions={[PAGE_SIZE]}
            sx={{
              mt: 1,
              '& .MuiDataGrid-row': {
                minHeight: '36px !important',
                maxHeight: '36px !important',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-cell': {
                py: 0,
              },
            }}
          />
        )}

        {selectedTemplateId && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
            Note: Loading will replace your current primary metrics, rule definitions, and rule classifications.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <Button onClick={handleClose} variant="text" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleLoad} disabled={!selectedTemplateId || isSubmitting} variant="contained" color="primary">
          {isSubmitting ? 'Loading...' : 'Load Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
